import type { Vec2 } from "kaplay";
import { startBatFlutter } from "../audio";

// A bonus bat that flutters across the sky on a curved (sine) path. Shooting it
// grants the player a temporary double-score bonus (handled by the game scene).

const BODY = [34, 26, 46] as const;
const WING = [20, 15, 30] as const;
const EYE = [220, 40, 40] as const;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function drawBat(t: number, phase: number) {
  const speed = 30; // rapid flap
  // each wing is segments that ripple outward (a wave traveling along the wing)
  for (const dir of [-1, 1] as const) {
    for (let s = 0; s < 3; s++) {
      const wave = Math.sin(t * speed + phase - s * 0.9);
      const x = dir * (7 + s * 7);
      const y = -wave * (2 + s * 3); // outer segments swing more
      drawRect({ pos: vec2(x, y), width: 9, height: 5, color: rgb(...WING), anchor: "center" });
    }
  }
  drawRect({ pos: vec2(0, 1), width: 9, height: 12, color: rgb(...BODY), anchor: "center" }); // body
  drawRect({ pos: vec2(-3, -7), width: 3, height: 4, color: rgb(...BODY), anchor: "center" }); // ear
  drawRect({ pos: vec2(3, -7), width: 3, height: 4, color: rgb(...BODY), anchor: "center" }); // ear
  drawRect({ pos: vec2(-2, -1), width: 2, height: 2, color: rgb(...EYE), anchor: "center" }); // eye
  drawRect({ pos: vec2(2, -1), width: 2, height: 2, color: rgb(...EYE), anchor: "center" }); // eye
}

function spawnBatPoof(x: number, y: number) {
  const p = add([pos(x, y), z(160)]);
  const max = 0.25;
  let life = max;
  p.onUpdate(() => {
    life -= dt();
    if (life <= 0) destroy(p);
  });
  p.onDraw(() => {
    const k = 1 - life / max;
    const op = life / max;
    drawCircle({ radius: 6 + 16 * k, color: rgb(60, 45, 80), opacity: op * 0.7 });
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const d = (8 + 18 * k);
      drawRect({ pos: vec2(Math.cos(a) * d, Math.sin(a) * d), width: 3, height: 3, color: rgb(34, 26, 46), opacity: op, anchor: "center" });
    }
  });
}

export interface Bat {
  isAlive(): boolean;
  contains(p: Vec2, margin: number): boolean;
  hit(): void;
}

export function addBat(opts: { fromLeft: boolean; isPaused: () => boolean; duration?: number }): Bat {
  const dur = opts.duration ?? 4.5;
  const startX = opts.fromLeft ? -30 : width() + 30;
  const endX = opts.fromLeft ? width() + 30 : -30;
  const baseY = 72;
  const amp = 46;
  const scl = 1.12; // 20% smaller than 1.4
  const phase = rand(0, Math.PI * 2);
  let p = 0;
  let alive = true;

  const obj = add([pos(startX, baseY), scale(scl), z(110), "bat"]);

  // rapid high C/G squeak that tracks the wingbeat; silenced while paused
  const stopFlutter = startBatFlutter(() => alive && !opts.isPaused());

  obj.onUpdate(() => {
    if (!alive || opts.isPaused()) return;
    p += dt() / dur;
    if (p >= 1) {
      alive = false;
      stopFlutter();
      destroy(obj);
      return;
    }
    obj.pos.x = lerp(startX, endX, p);
    obj.pos.y = baseY + Math.sin(p * Math.PI * 3) * amp; // curved flutter
  });
  obj.onDraw(() => drawBat(time(), phase));

  return {
    isAlive: () => alive,
    contains(pt, margin) {
      return Math.abs(pt.x - obj.pos.x) <= 18 * scl + margin && Math.abs(pt.y - obj.pos.y) <= 12 * scl + margin;
    },
    hit() {
      if (!alive) return;
      alive = false;
      stopFlutter();
      spawnBatPoof(obj.pos.x, obj.pos.y);
      destroy(obj);
    },
  };
}
